import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faHourglassHalf, faXmark, faCircleCheck, faCircleExclamation, faUser, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import ImageCropper from '../ImageCropper/ImageCropper';
import './PhotoChange.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

/* "My photo" card on the student / teacher Settings page.
   The new photo is cropped, sent, and waits for the admin's OK (the current one stays until then).
   role: 'student' | 'teacher'. onChange(): called after a photo was sent or cancelled. */
const PhotoChange = ({ role = 'student', onChange }) => {
  const [data, setData] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/${role}/photo`, auth());
      setData(res.data);
    } catch {
      setData({ image: null, pending: null, unavailable: true });
    }
  }, [role]);

  useEffect(() => {
    load();
  }, [load]);

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) return setMsg({ type: 'error', text: 'Please choose a photo (JPG or PNG).' });
    if (file.size > 10 * 1024 * 1024) return setMsg({ type: 'error', text: 'This photo is too large. Choose one under 10MB.' });
    setMsg(null);
    const reader = new FileReader();
    reader.onloadend = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    return undefined;
  };

  const send = async (file) => {
    setCropSrc(null);
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await axios.post(`${API}/api/${role}/photo`, fd, auth());
      setMsg({ type: 'success', text: 'Photo sent! It shows on your profile once RoboMind approves it.' });
      await load();
      if (onChange) onChange();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Couldn’t send your photo. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/api/${role}/photo`, auth());
      setMsg(null);
      await load();
      if (onChange) onChange();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Couldn’t cancel it.' });
    } finally {
      setBusy(false);
    }
  };

  // remove the current photo (no approval needed)
  const removePhoto = async () => {
    if (!window.confirm('Remove your photo? Your profile will show your initial instead.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/${role}/photo/current`, auth());
      setMsg({ type: 'success', text: 'Photo removed.' });
      await load();
      if (onChange) onChange();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Couldn’t remove your photo.' });
    } finally {
      setBusy(false);
    }
  };

  if (!data || data.unavailable) return null;

  return (
    <section className="rmpch" aria-labelledby={`rmpch-title-${role}`}>
      <h2 id={`rmpch-title-${role}`} className="rmpch-title">
        <FontAwesomeIcon icon={faCamera} /> My photo
      </h2>
      <div className="rmpch-row">
        <span className="rmpch-face">
          <span className="rmpch-clip">{data.image ? <img src={data.image} alt="" /> : <FontAwesomeIcon icon={faUser} />}</span>
        </span>
        {data.pending && (
          <span className="rmpch-face is-pending" title="Waiting for approval">
            <span className="rmpch-clip">
              <img src={data.pending} alt="" />
            </span>
            <i>
              <FontAwesomeIcon icon={faHourglassHalf} />
            </i>
          </span>
        )}
        <div className="rmpch-text">
          {data.pending ? (
            <p>
              <b>Your new photo is waiting for approval.</b> Your current photo stays until it’s approved.
            </p>
          ) : (
            <p>
              {role === 'student'
                ? 'Use a clear photo of yourself, where we can see your face. RoboMind checks every new photo before it shows.'
                : 'Use a clear, friendly photo of yourself. The admin approves every new photo before it shows.'}
            </p>
          )}
          <div className="rmpch-actions">
            <label className={`rmpch-btn ${busy ? 'is-busy' : ''}`}>
              <input type="file" accept="image/*" onChange={pick} disabled={busy} hidden />
              <FontAwesomeIcon icon={faCamera} /> {busy ? 'Sending…' : data.pending ? 'Choose another' : data.image ? 'Change photo' : 'Add a photo'}
            </label>
            {data.pending && (
              <button type="button" className="rmpch-btn rmpch-btn--ghost" onClick={cancel} disabled={busy}>
                <FontAwesomeIcon icon={faXmark} /> Cancel
              </button>
            )}
            {data.image && !data.pending && (
              <button type="button" className="rmpch-btn rmpch-btn--ghost" onClick={removePhoto} disabled={busy}>
                <FontAwesomeIcon icon={faTrashCan} /> Remove photo
              </button>
            )}
          </div>
        </div>
      </div>
      {msg && (
        <p className={`rmpch-msg is-${msg.type}`} role={msg.type === 'error' ? 'alert' : 'status'}>
          <FontAwesomeIcon icon={msg.type === 'success' ? faCircleCheck : faCircleExclamation} /> {msg.text}
        </p>
      )}
      {cropSrc && <ImageCropper src={cropSrc} title="Crop your photo" onCancel={() => setCropSrc(null)} onDone={send} />}
    </section>
  );
};

export default PhotoChange;
